import {
  Box,
  useEmailAddressValidator,
  usePhoneNumberValidator,
} from '@ace-clublabs/shared-component-library';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ContactInfoForm, {
  type ContactInfoFields,
} from '@/components/shared/ContactInfoForm';
import ErrorPage from '@/components/shared/ErrorPage';
import LoadingContainer from '@/components/shared/LoadingContainer';
import { constants } from '@/constants';
import { postProcessEmailInquiry } from '@/features/confirmation/api/postCreateEmailLead';
import { buildAdditionalNotes } from '@/features/confirmation/utils/buildAdditionalNotes';
import type { ProcessEmailInquiryData } from '@/features/confirmation/utils/ProcessEmailInquiryData';
import useLandingPageRedirection from '@/hooks/useLandingPageRedirection';
import { getClubCode, Page } from '@/lib/tagging';
import useTagging from '@/lib/tagging/useTagging';
import useAppointmentStore from '@/store/AppointmentStore';
import useContextAwarenessStore from '@/store/ContextAwarenessStore';
import useIdentityStore from '@/store/IdentityStore';
import useProgressStore from '@/store/ProgressStore';
import useSessionStore from '@/store/SessionStore';
import useThemeStore from '@/store/ThemeStore';
import useTSTStore from '@/store/TSTStore';

function EmailInquiry() {
  const navigateTo = useLandingPageRedirection();

  const { setProgress } = useProgressStore();
  const hasTagged = useRef(false);

  const [isProcessingEmailInquiry, setIsProcessingEmailInquiry] =
    useState(false);
  const [processEmailInquiryFailed, setProcessEmailInquiryFailed] =
    useState(false);
  const handleProcessEmailInquirySuccess = () => {
    setProgress(75);
  };

  const handleIsCreatingEmailInquiry = () => {
    setIsProcessingEmailInquiry(true);
  };

  const handleProcessEmailInquiryError = () => {
    setIsProcessingEmailInquiry(false);
    setProcessEmailInquiryFailed(true);
  };
  const navigate = useNavigate();

  const { sessionSource, sessionId, flowType } = useSessionStore();

  const {
    selectedDestination,
    searchedDestination,
    userSearch,
    contactInfo,
    setContactInfo,
    setConfirmationId,
  } = useAppointmentStore();

  const userInfo = useIdentityStore((state) => state.userInfo);

  const { isLuxuryTheme } = useThemeStore();

  const tagging = useTagging();

  // This is required for TRVL-1168 as our luxury experience requires both phone and email when making an inquiry,
  // while for 'regular' experience, the required fields are based on communication preference.
  const isPhoneNumberRequired =
    isLuxuryTheme || contactInfo.communicationPreference === 'Phone';
  const isEmailRequired =
    isLuxuryTheme || contactInfo.communicationPreference === 'Email';

  const phoneNumberValidator = usePhoneNumberValidator({
    label: 'Phone Number',
    required: isPhoneNumberRequired,
  });
  const emailValidator = useEmailAddressValidator({
    label: 'Email',
    required: isEmailRequired,
  });

  const regularFields: ContactInfoFields[] = [
    'firstName',
    'lastName',
    'zipCode',
    'communicationPreference',
    'phoneNumber',
    'email',
    'expectedTravelTime',
    'typeOfTrip',
    'emailMessage',
  ];

  const luxuryFields: ContactInfoFields[] = [
    'firstName',
    'lastName',
    'zipCode',
    'communicationPreference',
    'phoneNumber',
    'email',
    'emailMessage',
  ];

  useEffect(() => {
    if (!hasTagged.current) {
      const trvEndLocation =
        searchedDestination || userSearch || selectedDestination.name;

      tagging.tag('view', sessionSource, {
        page: Page.contactInfo,
        sessionid: sessionId,
        flow: flowType,
        trv_end_location: trvEndLocation,
        comm_pref: 'email',
      });

      hasTagged.current = true;
    }
  }, [
    sessionSource,
    sessionId,
    flowType,
    searchedDestination,
    selectedDestination.name,
    tagging.tag,
    userSearch,
  ]);

  const { TSTParams } = useTSTStore();
  const contextAwareness = useContextAwarenessStore(
    (state) => state.contextAwareness,
  );

  const processEmailInquiry = useMutation({
    mutationFn: (emailData: ProcessEmailInquiryData) =>
      postProcessEmailInquiry(emailData),
    onSuccess: (responseData) => {
      setConfirmationId(responseData.incidentId);
      handleProcessEmailInquirySuccess();
      setTimeout(() => {
        navigate('/email-confirmation', { replace: true });
      }, 250);
    },
    onError: (err) => {
      if (err.message !== constants.TANSTACK_ABORTED_ERROR)
        handleProcessEmailInquiryError();
    },
  });

  const handleFormSubmit = () => {
    handleIsCreatingEmailInquiry();

    const destinationNote = searchedDestination || userSearch;

    const emailData: ProcessEmailInquiryData = {
      clubCode: getClubCode(),
      destination: selectedDestination.destinationApiName ?? '',
      email: contactInfo.email ?? '',
      firstName: contactInfo.firstName ?? '',
      lastName: contactInfo.lastName ?? '',
      phone: contactInfo.phoneNumber ?? '',
      emailMessage: buildAdditionalNotes({
        destinationNote,
        userNotes: contactInfo.emailMessage ?? '',
        card: contextAwareness.card,
        sourceUrl: contextAwareness.sourceUrl,
      }),
      whenDoYouPlanToTravel: contactInfo.expectedTravelTime ?? '',
      zipCode: contactInfo.zipCode ?? '',
      typeOfTripDesired: contactInfo.typeOfTrip ?? [],
      AAAMemberFlag: !!userInfo?.customerId,
      membershipNo: userInfo?.memberNumber ?? '',
      trafficSource: window.aceMediaTagValues.traffic_source ?? '',
      trafficSourceCategory: window.aceMediaTagValues.traffic_category ?? '',
      websiteSource: window.aceMediaTagValues.website_source ?? '',
      MACSCustId: userInfo?.customerId || '',
      TSTTripURL: TSTParams.TSTTripURL,
      preferredContactMethod: contactInfo.communicationPreference ?? '',
      isLuxuryFlow: isLuxuryTheme,
    };

    processEmailInquiry.mutate(emailData);
  };

  if (isProcessingEmailInquiry) {
    return <LoadingContainer message={'Sending your request...'} />;
  }

  if (processEmailInquiryFailed) {
    return <ErrorPage onClickTryAgain={() => navigate(navigateTo)} />;
  }

  return (
    <Box
      sx={[
        {
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          backgroundColor: 'background.default',
        },
      ]}
    >
      <ContactInfoForm
        formHeader={'How should we contact you?'}
        fields={isLuxuryTheme ? luxuryFields : regularFields}
        formValues={contactInfo}
        updateField={setContactInfo}
        onSubmit={handleFormSubmit}
        fieldsProps={{
          phoneNumber: {
            validator: phoneNumberValidator,
            label: isPhoneNumberRequired ? 'Phone Number*' : 'Phone Number',
            key: `phoneNumber-${isPhoneNumberRequired}`,
          },
          email: {
            validator: emailValidator,
            label: isEmailRequired ? 'Email*' : 'Email',
            key: `email-${isEmailRequired}`,
          },
        }}
      />
    </Box>
  );
}

export default EmailInquiry;
